import {
  faArrowDownToLine,
  faChartSimple,
  faCircleExclamation,
  faCode,
  faDownload,
  faTable,
} from "@fortawesome/pro-regular-svg-icons";
import { format } from "sql-formatter";

import type { PinPreviewData } from "src/analytics/dashboard/PinChartModal";
import {
  type MetricsQueryParams,
  sqlRequiresParams,
  toBindParams,
  useMetricsQuery,
} from "src/analytics/metrics/useMetricsQuery";
import { ChatChartCard } from "src/analytics/metrics/VisualizeMetrics/ChatChartCard";
import { MetricsDataTable } from "src/analytics/metrics/VisualizeMetrics/MetricsDataTable";
import type { LabelDictionaries } from "src/analytics/metrics/VisualizeMetrics/specialDomainScaling";
import { ChatTextContent } from "src/base-components/Chat/ChatTextContent";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { extractTitle } from "src/chartSandbox/SandboxedChart";
import {
  type ToolCallContentBlock,
  type ToolMessage,
} from "src/clients/agent-management";
import type { QueryContext } from "src/clients/analytics-query";
import { EmptyState } from "src/design-system/EmptyState";
import { Skeleton } from "src/design-system/Skeleton";
import { type TabItem, Tabs } from "src/design-system/Tabs";
import { downloadRowsAsCsv } from "src/utils/csvExport";

type VisualizeMetricsArgs = {
  sql?: string;
  chartDefinition?: Record<string, unknown>;
  params?: MetricsQueryParams;
  message?: string;
};

const asChartDefinition = (
  value: unknown,
): Record<string, unknown> | undefined =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;

// Prefer the backend-processed spec on toolResult.artifact (carries the
// semantic palette and role-driven scales). Fall back to the raw LLM tool-call
// args for pre-artifact agents and for plot mode.
const getArgs = (
  toolCall: ToolCallContentBlock,
  toolResult: ToolMessage | null,
): VisualizeMetricsArgs => {
  const args = toolCall.args as Record<string, unknown>;
  const artifactChart = asChartDefinition(
    toolResult?.artifact?.chart_definition,
  );
  return {
    sql: typeof args.sql === "string" ? args.sql : undefined,
    chartDefinition: artifactChart ?? asChartDefinition(args.chart_definition),
    params: toBindParams(toolResult?.artifact?.parameters),
    message: typeof args.message === "string" ? args.message : undefined,
  };
};

const ChartPanel: React.FC<{
  sql: string | undefined;
  chartDefinition: Record<string, unknown> | undefined;
  labelDictionaries: LabelDictionaries;
  params: MetricsQueryParams | undefined;
  onRequestPin?: (data: PinPreviewData) => void;
  onUnpin?: () => void;
  pinDisabled?: boolean;
  pinDisabledReason?: string;
  dataSource?: QueryContext;
}> = ({
  sql,
  chartDefinition,
  labelDictionaries,
  params,
  onRequestPin,
  onUnpin,
  pinDisabled,
  pinDisabledReason,
  dataSource,
}) => {
  const {
    data: rows,
    isLoading,
    error,
  } = useMetricsQuery(sql, dataSource, params);
  // Tool call still in flight: the SQL is parameterized but its params (from the
  // result artifact) haven't arrived — the query is held, so show loading.
  const awaitingParams = sqlRequiresParams(sql) && params === undefined;

  if (!sql) {
    return (
      <EmptyState
        description="Provide a SQL query to see results."
        headline="No SQL"
        icon={faCode}
        size="compact"
      />
    );
  }
  if (!chartDefinition) {
    return (
      <EmptyState
        description="Provide a chart definition to render a visualization."
        headline="No chart definition"
        icon={faChartSimple}
        size="compact"
      />
    );
  }
  if (isLoading || awaitingParams) {
    return <Skeleton height="h-full" width="w-full" />;
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        description="The query returned no rows."
        headline="No data"
        icon={faChartSimple}
        size="compact"
      />
    );
  }

  return (
    <ChatChartCard
      chartDefinition={chartDefinition}
      labelDictionaries={labelDictionaries}
      onRequestPin={onRequestPin}
      onUnpin={onUnpin}
      pinDisabled={pinDisabled}
      pinDisabledReason={pinDisabledReason}
      rows={rows}
    />
  );
};

const formatSql = (sql: string): string => {
  try {
    return format(sql, { language: "postgresql" });
  } catch {
    return sql;
  }
};

const SqlPanel: React.FC<{ sql: string | undefined }> = ({ sql }) => {
  if (!sql) {
    return <div className="font-inter-normal-12px text-gray-400">No SQL</div>;
  }
  return (
    <CodeEditor language="sql" lineWrapping readOnly value={formatSql(sql)} />
  );
};

const TablePanel: React.FC<{
  sql: string | undefined;
  params: MetricsQueryParams | undefined;
  dataSource?: QueryContext;
}> = ({ sql, params, dataSource }) => {
  const {
    data: rows,
    isLoading,
    error,
  } = useMetricsQuery(sql, dataSource, params);
  // See ChartPanel: hold the loading state while a parameterized query waits
  // for its params instead of flashing an empty "No data".
  const awaitingParams = sqlRequiresParams(sql) && params === undefined;

  if (!sql) {
    return (
      <EmptyState
        description="Provide a SQL query to see results."
        headline="No SQL"
        icon={faCode}
        size="compact"
      />
    );
  }
  if (error) {
    return (
      <EmptyState
        description={error.message}
        headline="Query failed"
        icon={faCircleExclamation}
        size="compact"
        variant="error"
      />
    );
  }
  if (!isLoading && !awaitingParams && (!rows || rows.length === 0)) {
    return (
      <EmptyState
        description="The query returned no rows."
        headline="No data"
        icon={faTable}
        size="compact"
      />
    );
  }

  return (
    <MetricsDataTable
      isLoading={isLoading || awaitingParams}
      rows={rows ?? []}
    />
  );
};

export const VisualizeMetricsToolMessage: React.FC<{
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
  labelDictionaries: LabelDictionaries;
  onRequestPin?: (
    sql: string,
    preview: PinPreviewData,
    parameterNames: string[],
  ) => void;
  onUnpin?: () => void;
  pinDisabled?: boolean;
  pinDisabledReason?: string;
  dataSource?: QueryContext;
}> = ({
  toolCall,
  toolResult,
  labelDictionaries,
  onRequestPin,
  onUnpin,
  pinDisabled,
  pinDisabledReason,
  dataSource,
}) => {
  const { sql, chartDefinition, params, message } = getArgs(
    toolCall,
    toolResult,
  );
  // same query key as the panels — shares the cache, not a second fetch
  const { data: rows, error } = useMetricsQuery(sql, dataSource, params);
  const description = message?.trim();
  const handleRequestPin =
    onRequestPin && sql && chartDefinition
      ? (preview: PinPreviewData) =>
          onRequestPin(sql, preview, Object.keys(params ?? {}))
      : undefined;

  const canExport = !error && !!rows && rows.length > 0;
  const handleExportCsv = () => {
    if (rows && rows.length > 0) {
      downloadRowsAsCsv(
        rows,
        chartDefinition ? extractTitle(chartDefinition) : "table",
      );
    }
  };

  const panel = (content: React.ReactNode) => (
    <div className="h-80 w-full pt-3">{content}</div>
  );

  const tabs: TabItem[] = [
    {
      key: "chart",
      label: "Chart",
      content: panel(
        <ChartPanel
          chartDefinition={chartDefinition}
          dataSource={dataSource}
          labelDictionaries={labelDictionaries}
          onRequestPin={handleRequestPin}
          onUnpin={onUnpin}
          params={params}
          pinDisabled={pinDisabled}
          pinDisabledReason={pinDisabledReason}
          sql={sql}
        />,
      ),
    },
    { key: "sql", label: "SQL", content: panel(<SqlPanel sql={sql} />) },
    {
      key: "table",
      label: "Table",
      suffix: canExport
        ? {
            type: "ellipsis",
            icon: faArrowDownToLine,
            dataLoc: "visualize-metrics-table-actions",
            elements: [
              { key: "Export CSV", icon: faDownload, action: handleExportCsv },
            ],
          }
        : undefined,
      content: panel(
        <TablePanel dataSource={dataSource} params={params} sql={sql} />,
      ),
    },
  ];

  return (
    <div className="w-full" data-loc="visualize-metrics-tool-message">
      <Tabs defaultSelectedKey="chart" tabs={tabs} />
      {description && (
        <ChatTextContent
          content={description}
          dataLoc="visualize-metrics-message"
          delay={0}
          useMarkdownRenderer
        />
      )}
    </div>
  );
};
